/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Profile / Account pane (Figma node 3542:7348). Real profile editor backed by
// `PATCH /v1/me/profile`:
//   * Profile header — avatar (Change) + inline display-name edit.
//   * Profile details — bio, job title, company, contact email/phone (one Save).
//   * Discoverability — "Visible in search" toggle (`discoverable`).
//   * Identity verification — verified badge, else an OTP "Verify" CTA.
//   * KYC (Persona) — personal identity verification via hosted flow.
//   * Recovery passphrase — Ping is passwordless; this opens the recovery
//     backup flow (the real account-protection mechanism).
//   * Deactivate (unchanged placeholder).

import { ImageIcon } from "lucide-react-native";
import { useCallback, useEffect, useRef, useState } from "react";
import { Animated, Easing, Image, Platform, Text, View } from "react-native";
import { PillButton, TextField, Toggle } from "../controls";
import { SettingsDivider, SettingsRow } from "../SettingsRow";

/**
 * Pulsing shimmer overlay shown over the avatar while a newly-picked image is
 * uploading. Animated opacity loop so it reads as "working" on both web
 * (react-native-web) and native desktop (react-native-macos), where NativeWind's
 * `animate-pulse` doesn't drive a JS animation.
 */
function AvatarUploadingShimmer(): JSX.Element {
  const pulse = useRef(new Animated.Value(0.35)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 0.75,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: Platform.OS !== "web",
        }),
        Animated.timing(pulse, {
          toValue: 0.35,
          duration: 700,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: Platform.OS !== "web",
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);
  return (
    <Animated.View
      accessibilityLabel="Uploading photo"
      className="absolute inset-0 items-center justify-center bg-light-surface-tertiary dark:bg-[#222429]"
      style={{ opacity: pulse }}
    >
      <ImageIcon size={24} strokeWidth={1.5} color="#8C929E" />
    </Animated.View>
  );
}

/** Mutable profile fields the pane can save. All optional → only changed
 *  fields are sent; an empty string clears a field server-side. */
export interface ProfileEditInput {
  readonly displayName?: string;
  readonly bio?: string;
  readonly jobTitle?: string;
  readonly companyName?: string;
  readonly contactEmail?: string;
  readonly contactPhone?: string;
}

export interface AccountPaneProps {
  readonly initialDisplayName: string;
  readonly email: string;
  readonly phone: string;
  readonly avatarUrl?: string;
  /** True while a freshly-picked avatar is uploading → shows a shimmer. */
  readonly avatarUploading?: boolean;
  readonly bio?: string;
  readonly jobTitle?: string;
  readonly company?: string;
  readonly discoverable?: boolean;
  readonly onDisplayNameSaved: (next: string) => void;
  readonly onUpdateProfile: (input: ProfileEditInput) => Promise<void>;
  readonly onAvatarPicked?: () => void | Promise<void>;
  readonly onAvatarPress?: () => void;
  /** Toggle search discoverability (`discoverable`). */
  readonly onToggleDiscoverable?: (next: boolean) => Promise<void> | void;
  /**
   * Persona KYC status from `GET /v1/me/kyc`.
   * When omitted the KYC row is hidden (Persona not configured).
   */
  readonly kycStatus?:
    | "none"
    | "pending"
    | "needs_review"
    | "approved"
    | "declined";
  /**
   * Initiates KYC: the host creates the inquiry (`POST /v1/me/kyc`) and
   * opens the embedded Persona flow in an in-page modal. When omitted the
   * "Start verification" button is disabled.
   */
  readonly onKycStart?: () => Promise<void>;
  /** Open the recovery-passphrase backup flow (the "password" replacement). */
  readonly onOpenRecovery?: () => void;
  /** Open the Set Status overlay from within the Account pane. */
  readonly onSetStatusPress?: () => void;
  /** Current status to display in the row (emoji + text). */
  readonly currentStatusEmoji?: string;
  readonly currentStatusText?: string;
}

export function AccountPane(props: AccountPaneProps): JSX.Element {
  const {
    initialDisplayName,
    email,
    phone,
    avatarUrl,
    avatarUploading = false,
    bio,
    jobTitle,
    company,
    discoverable,
    onDisplayNameSaved,
    onUpdateProfile,
    onAvatarPicked,
    onAvatarPress,
    onToggleDiscoverable,
    kycStatus,
    onKycStart,
    onOpenRecovery,
    onSetStatusPress,
    currentStatusEmoji,
    currentStatusText,
  } = props;

  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [savingName, setSavingName] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Profile-details drafts (bio / job / company / email / phone).
  const [bioDraft, setBioDraft] = useState(bio ?? "");
  const [jobDraft, setJobDraft] = useState(jobTitle ?? "");
  const [companyDraft, setCompanyDraft] = useState(company ?? "");
  const [emailDraft, setEmailDraft] = useState(email);
  const [phoneDraft, setPhoneDraft] = useState(phone);
  const [savingDetails, setSavingDetails] = useState(false);

  const [discoverableOn, setDiscoverableOn] = useState(discoverable ?? false);

  // KYC state — tracks the in-flight start request + any error. The flow
  // itself renders in the host's embedded Persona modal.
  const [kycBusy, setKycBusy] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);

  useEffect(() => setDisplayName(initialDisplayName), [initialDisplayName]);
  useEffect(() => setBioDraft(bio ?? ""), [bio]);
  useEffect(() => setJobDraft(jobTitle ?? ""), [jobTitle]);
  useEffect(() => setCompanyDraft(company ?? ""), [company]);
  useEffect(() => setEmailDraft(email), [email]);
  useEffect(() => setPhoneDraft(phone), [phone]);
  useEffect(() => setDiscoverableOn(discoverable ?? false), [discoverable]);

  const handleSaveDisplayName = useCallback(async () => {
    const trimmed = displayName.trim();
    if (trimmed.length === 0) {
      setError("Display name is required.");
      return;
    }
    setSavingName(true);
    setError(null);
    try {
      await onUpdateProfile({ displayName: trimmed });
      onDisplayNameSaved(trimmed);
      setEditingName(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingName(false);
    }
  }, [displayName, onDisplayNameSaved, onUpdateProfile]);

  const detailsDirty =
    bioDraft !== (bio ?? "") ||
    jobDraft !== (jobTitle ?? "") ||
    companyDraft !== (company ?? "") ||
    emailDraft !== email ||
    phoneDraft !== phone;

  const handleSaveDetails = useCallback(async () => {
    setSavingDetails(true);
    setError(null);
    try {
      await onUpdateProfile({
        bio: bioDraft.trim(),
        jobTitle: jobDraft.trim(),
        companyName: companyDraft.trim(),
        contactEmail: emailDraft.trim(),
        contactPhone: phoneDraft.trim(),
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingDetails(false);
    }
  }, [
    bioDraft,
    jobDraft,
    companyDraft,
    emailDraft,
    phoneDraft,
    onUpdateProfile,
  ]);

  const handlePickAvatar = useCallback(() => {
    if (onAvatarPress !== undefined) {
      onAvatarPress();
      return;
    }
    if (onAvatarPicked !== undefined) void onAvatarPicked();
  }, [onAvatarPicked, onAvatarPress]);

  const handleToggleDiscoverable = useCallback(
    (next: boolean) => {
      setDiscoverableOn(next);
      void Promise.resolve(onToggleDiscoverable?.(next)).catch(() => {
        // Revert the optimistic toggle on failure.
        setDiscoverableOn(!next);
      });
    },
    [onToggleDiscoverable],
  );

  // Kick off verification. The host creates the inquiry and opens the
  // embedded Persona modal in-page (no tab/window); we just reflect the
  // in-flight + error state here.
  const handleKycStart = useCallback(async () => {
    if (onKycStart === undefined) return;
    setKycBusy(true);
    setKycError(null);
    try {
      await onKycStart();
    } catch (err) {
      setKycError((err as Error).message ?? "Failed to start verification.");
    } finally {
      setKycBusy(false);
    }
  }, [onKycStart]);

  return (
    <View className="flex flex-col gap-4">
      {/* Profile picture + display name. */}
      <SettingsRow
        variant="hero"
        title="Profile"
        description="This is how you appear to others on the app"
        testID="settings-account-profile"
      >
        <View className="flex flex-row items-center gap-4">
          <View className="w-[87px] h-[87px] rounded-2xl overflow-hidden bg-light-surface-secondary dark:bg-[#15171C] flex items-center justify-center">
            {/* Shimmer while the freshly-picked image uploads. */}
            {avatarUploading ? (
              <AvatarUploadingShimmer />
            ) : (
              <>
                {avatarUrl != null && avatarUrl.length > 0 ? (
                  <Image
                    source={{ uri: avatarUrl }}
                    className="w-full h-full"
                    resizeMode="cover"
                    accessibilityLabel=""
                  />
                ) : (
                  <ImageIcon size={28} strokeWidth={1.5} color="#6B7280" />
                )}
              </>
            )}
          </View>
          {onAvatarPicked !== undefined && (
            <PillButton
              variant="secondary"
              onClick={handlePickAvatar}
              disabled={avatarUploading}
            >
              {avatarUploading ? "Uploading…" : "Change"}
            </PillButton>
          )}
        </View>

        {editingName ? (
          <View className="flex flex-row items-end gap-2 pr-[94px] max-w-[655px]">
            <View className="flex-1 max-w-[475px]">
              <TextField
                label="Display name"
                value={displayName}
                onChange={setDisplayName}
                placeholder="Your name"
                autoComplete="name"
                maxLength={64}
              />
            </View>
            <PillButton
              variant="primary"
              onClick={handleSaveDisplayName}
              busy={savingName}
              disabled={
                displayName.trim().length === 0 ||
                displayName.trim() === initialDisplayName
              }
            >
              Save
            </PillButton>
            <PillButton
              variant="ghost"
              onClick={() => {
                setEditingName(false);
                setDisplayName(initialDisplayName);
                setError(null);
              }}
            >
              Cancel
            </PillButton>
          </View>
        ) : (
          <View className="flex flex-row items-end gap-2 pr-[94px] max-w-[655px]">
            <View className="flex-1 max-w-[475px]">
              <TextField
                label="Display name"
                value={initialDisplayName || "Not set"}
                onChange={() => undefined}
                disabled
              />
            </View>
            <PillButton
              variant="secondary"
              onClick={() => setEditingName(true)}
            >
              Change
            </PillButton>
          </View>
        )}
      </SettingsRow>

      <SettingsDivider />

      {/* Profile details — bio, job, company, contact email/phone. */}
      <SettingsRow
        title="Profile details"
        description="Shown on your profile to people you chat with"
        testID="settings-account-details"
      >
        <View className="max-w-[475px] gap-3">
          <TextField
            label="Bio"
            value={bioDraft}
            onChange={setBioDraft}
            placeholder="Tell people a little about yourself"
            maxLength={500}
          />
          <TextField
            label="Job title"
            value={jobDraft}
            onChange={setJobDraft}
            placeholder="e.g. Software Engineer"
            maxLength={100}
          />
          <TextField
            label="Company"
            value={companyDraft}
            onChange={setCompanyDraft}
            placeholder="e.g. Zenith Dynamics"
            maxLength={100}
          />
          <TextField
            label="Contact email"
            value={emailDraft}
            onChange={setEmailDraft}
            placeholder="Add a contact email"
            type="email"
            autoComplete="email"
          />
          <TextField
            label="Contact phone"
            value={phoneDraft}
            onChange={setPhoneDraft}
            placeholder="Add a contact phone number"
            type="tel"
            autoComplete="tel"
          />
          <View className="flex flex-row">
            <PillButton
              variant="primary"
              onClick={handleSaveDetails}
              busy={savingDetails}
              disabled={!detailsDirty}
            >
              Save changes
            </PillButton>
          </View>
        </View>
      </SettingsRow>

      <SettingsDivider />

      {/* Discoverability. */}
      <SettingsRow
        title="Visible in search"
        description="Let people find you by name in user search"
        testID="settings-account-discoverable"
      >
        <Toggle
          checked={discoverableOn}
          onChange={handleToggleDiscoverable}
          disabled={onToggleDiscoverable === undefined}
          label="Visible in search"
        />
      </SettingsRow>

      <SettingsDivider />

      {/* Status — set emoji + text status visible to contacts. */}
      <SettingsRow
        title="Status"
        description="Set a status message that appears on your profile"
        testID="settings-account-status"
      >
        <View className="flex flex-row items-center gap-3">
          {currentStatusEmoji || currentStatusText ? (
            <Text className="text-[14px] leading-[20px] text-light-text-primary dark:text-[#F5F6F8]">
              {currentStatusEmoji ? `${currentStatusEmoji} ` : ""}
              {currentStatusText ?? ""}
            </Text>
          ) : (
            <Text className="text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]">
              No status set
            </Text>
          )}
          <PillButton
            variant="secondary"
            onClick={() => onSetStatusPress?.()}
            disabled={onSetStatusPress === undefined}
          >
            {currentStatusEmoji || currentStatusText
              ? "Change status"
              : "Set status"}
          </PillButton>
        </View>
      </SettingsRow>

      <SettingsDivider />

      {/* KYC — personal identity verification via Persona. Hidden when the
			    host doesn't supply `kycStatus` (Persona not configured). */}
      {kycStatus !== undefined && (
        <>
          <SettingsRow
            title="Identity verification (KYC)"
            description="Verify your identity to unlock trusted features. Powered by Persona."
          >
            {kycStatus === "approved" ? (
              <View className="flex flex-row items-center gap-2">
                <Text className="text-[14px] leading-[20px] font-semibold text-[#3CCB7F]">
                  Verified ✓
                </Text>
              </View>
            ) : kycStatus === "pending" || kycStatus === "needs_review" ? (
              <View className="flex flex-col gap-2">
                <Text className="text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]">
                  {kycStatus === "needs_review"
                    ? "Under review — we'll notify you when done."
                    : "Verification in progress…"}
                </Text>
                {/* A stalled/abandoned inquiry stays "pending" forever
								    (the webhook only fires on completion), so always
								    offer a restart — POST /v1/me/kyc mints a fresh
								    Persona inquiry and reopens the popup. */}
                <View className="flex flex-row">
                  <PillButton
                    variant="secondary"
                    onClick={handleKycStart}
                    busy={kycBusy}
                    disabled={onKycStart === undefined || kycBusy}
                  >
                    Restart verification
                  </PillButton>
                </View>
              </View>
            ) : kycStatus === "declined" ? (
              <View className="flex flex-col gap-2">
                <Text className="text-[14px] leading-[20px] text-[#FF5353]">
                  Verification declined.
                </Text>
                <View className="flex flex-row">
                  <PillButton
                    variant="primary"
                    onClick={handleKycStart}
                    busy={kycBusy}
                    disabled={onKycStart === undefined || kycBusy}
                  >
                    Try again
                  </PillButton>
                </View>
              </View>
            ) : (
              <View className="flex flex-col gap-2">
                <Text className="text-[14px] leading-[20px] text-light-text-secondary dark:text-[#B5BAC3]">
                  Not verified
                </Text>
                <View className="flex flex-row">
                  <PillButton
                    variant="primary"
                    onClick={handleKycStart}
                    busy={kycBusy}
                    disabled={onKycStart === undefined || kycBusy}
                  >
                    Start identity verification
                  </PillButton>
                </View>
              </View>
            )}
            {kycError != null && (
              <Text className="text-[12px] leading-[16px] text-[#FF5353] mt-1">
                {kycError}
              </Text>
            )}
          </SettingsRow>

          <SettingsDivider />
        </>
      )}

      {/* Recovery passphrase (Ping is passwordless). */}
      <SettingsRow
        title="Recovery passphrase"
        description="Ping has no password — your account is protected by a recovery passphrase. Back it up to restore on a new device."
        testID="settings-account-recovery"
      >
        <View className="flex flex-row">
          <PillButton
            variant="secondary"
            onClick={() => onOpenRecovery?.()}
            disabled={onOpenRecovery === undefined}
          >
            Manage recovery
          </PillButton>
        </View>
      </SettingsRow>

      <SettingsDivider />

      {/* Deactivate. */}
      <SettingsRow
        title="Deactivate your account"
        description="Deactivating your account is permanent ― be careful!"
      >
        <View className="flex flex-row">
          <PillButton variant="destructive" onClick={() => undefined} disabled>
            Deactivate account
          </PillButton>
        </View>
      </SettingsRow>

      {error != null && (
        <Text className="text-[12px] leading-[16px] text-[#FF5353]">
          {error}
        </Text>
      )}
    </View>
  );
}
